import { Component, HostBinding, computed, inject, input } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'; // cspell:disable-line
import { LucideAngularModule } from 'lucide-angular';
import { DurationFormatPipe } from 'src/app/pipes/duration-format.pipe';
import {
  TransportAlternativeRoute,
  TransportAlternativeRouteLeg,
} from '../../../models/reduction-tip';
import { VEHICLE_CATEGORY_ICON } from '../../../constants/reduction-tip-icons';
import { ImpactService } from '../../../services/impact.service';

/**
 * The vehicles of one confirmed itinerary, in the order they are taken, then
 * where to look it up and book it. Shared by the steps unfolded under a
 * flight's choice chips and by the routes dialog. Projected content lands at
 * the end of the links row.
 */
@Component({
  selector: 'app-route-legs',
  standalone: true,
  imports: [TranslocoModule, LucideAngularModule],
  providers: [DurationFormatPipe],
  templateUrl: './route-legs.component.html',
})
export class RouteLegsComponent {
  @HostBinding('class') readonly hostClass = 'flex flex-col gap-2.5';

  route = input.required<TransportAlternativeRoute>();

  private impactService = inject(ImpactService);
  private translocoService = inject(TranslocoService);
  private durationPipe = inject(DurationFormatPipe);

  /**
   * Operators of the itinerary that published a site — a traveller books on
   * the operator's own site, not on the map.
   */
  operators = computed(() => {
    const byUrl = new Map<string, string>();
    for (const leg of this.route().legs) {
      if (leg.operatorUrl && leg.operator && !byUrl.has(leg.operatorUrl)) {
        byUrl.set(leg.operatorUrl, leg.operator);
      }
    }
    return [...byUrl].map(([url, name]) => ({ name, url }));
  });

  vehicleIcon(transportType: string): string {
    const category = this.impactService.getTransportCategory(transportType);
    return (category && VEHICLE_CATEGORY_ICON[category]) ?? 'tram-front';
  }

  /**
   * Everything identifying one boarding, on a single line: which line, where it
   * is headed, who runs it, how long it takes. Parts Google left empty are
   * dropped rather than shown as blanks.
   */
  legDetails(leg: TransportAlternativeRouteLeg): string {
    const stopKey = leg.stops === 1 ? 'stop' : 'stops';
    const stops =
      leg.stops > 0
        ? this.translocoService.translate(`reductionTips.route.${stopKey}`, {
            count: leg.stops,
          })
        : null;
    const headsign = leg.headsign
      ? this.translocoService.translate('reductionTips.route.direction', {
          headsign: leg.headsign,
        })
      : null;

    return [
      leg.line || leg.vehicleName,
      headsign,
      leg.operator,
      this.durationPipe.transform(leg.durationHours),
      stops,
    ]
      .filter(Boolean)
      .join(' · ');
  }
}
